import {
	DEFAULT_IMAGE_DURATION_MS,
	DEFAULT_MESSAGE_DURATION_MS,
	DEFAULT_WEATHER_DURATION_MS,
	MAX_SLIDE_DURATION_MS,
	MIN_SLIDE_DURATION_MS,
} from "./constants";

export type SlideItemLike = {
	type: "photo" | "message" | "animation" | "weather" | "weather_card";
	data: Record<string, unknown>;
};

export type SlideTimingSettings = {
	messageDurationMs?: number;
	imageDurationMs?: number;
	weatherDurationMs?: number;
};

function clamp(value: number, min: number, max: number): number {
	if (value < min) return min;
	if (value > max) return max;
	return value;
}

function sanitizeDuration(value: unknown): number | null {
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
		return null;
	}
	return clamp(Math.round(value), MIN_SLIDE_DURATION_MS, MAX_SLIDE_DURATION_MS);
}

/**
 * Résout la durée d'affichage d'un slide en ms.
 *
 * Priorité :
 *   1. Surcharge par item (`item.data.display_duration_ms`).
 *   2. Réglage du board selon le type (`messageDurationMs` / `imageDurationMs`).
 *   3. Animation : `item.data.duration` (en secondes, champ historique).
 *   4. Défaut codé en dur.
 *
 * Toutes les valeurs sont clampées dans `[MIN_SLIDE_DURATION_MS, MAX_SLIDE_DURATION_MS]`.
 */
export function resolveSlideDuration(
	item: SlideItemLike,
	settings: SlideTimingSettings = {},
): number {
	const override = sanitizeDuration(item.data.display_duration_ms);
	if (override !== null) return override;

	if (item.type === "message") {
		return sanitizeDuration(settings.messageDurationMs) ?? DEFAULT_MESSAGE_DURATION_MS;
	}
	if (item.type === "photo") {
		return sanitizeDuration(settings.imageDurationMs) ?? DEFAULT_IMAGE_DURATION_MS;
	}
	if (item.type === "weather" || item.type === "weather_card") {
		return sanitizeDuration(settings.weatherDurationMs) ?? DEFAULT_WEATHER_DURATION_MS;
	}
	// Animations : champ historique `duration` exprimé en secondes.
	const animSeconds = item.data.duration;
	if (typeof animSeconds === "number" && Number.isFinite(animSeconds) && animSeconds > 0) {
		return clamp(Math.round(animSeconds * 1000), MIN_SLIDE_DURATION_MS, MAX_SLIDE_DURATION_MS);
	}
	return DEFAULT_IMAGE_DURATION_MS;
}
